import React, { useState, useEffect } from "react";
import DSBox from "views/system-design/DSBox";
import DSTextField from "views/system-design/DSTextField";
import DSButton from "views/system-design/DSButton";
import DSSwitch from "views/system-design/DSSwitch";
import DSFormControlLabel from "views/system-design/DSFormControlLabel";
import DSTypography from "views/system-design/DSTypography";
import DSCircularProgress from "views/system-design/DSCircularProgress";
import DSCheckbox from "views/system-design/DSCheckBox";
import type { DeviceInfo } from "../types";
import { versionTesterApi } from "../api/versionTesterApi";
import StorySelector from "./StorySelector";

interface TestFormProps {
  devices: DeviceInfo[];
  onTestStarted: () => void;
  disabled?: boolean;
}

/**
 * Form component for configuring and running firmware tests
 *
 * Allows user to:
 * - Enter target firmware version
 * - Toggle factory reset per device
 * - Trigger test execution
 *
 * Displays loading state during submission and error messages on failure.
 */
const TestForm: React.FC<TestFormProps> = ({
  devices,
  onTestStarted,
  disabled = false,
}) => {
  const [targetVersion, setTargetVersion] = useState("");
  const [factoryResetToggles, setFactoryResetToggles] = useState<
    Record<string, boolean>
  >({});
  const [selectedStoryIds, setSelectedStoryIds] = useState<string[]>([]);
  const [skipInternet, setSkipInternet] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize factory reset toggles when devices change
  useEffect(() => {
    setFactoryResetToggles((prev) => {
      const newToggles: Record<string, boolean> = { ...prev };
      devices.forEach((device) => {
        // Preserve existing values, default new devices to false
        if (!(device.id in newToggles)) {
          newToggles[device.id] = false;
        }
      });
      return newToggles;
    });
  }, [devices]);

  const handleToggle = (deviceId: string) => {
    setFactoryResetToggles((prev) => ({
      ...prev,
      [deviceId]: !prev[deviceId],
    }));
  };

  const handleStorySelectionChange = (ids: string[]) => {
    setSelectedStoryIds(ids);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      await versionTesterApi.runTest({
        targetVersion,
        devices: devices.map((d) => ({
          deviceId: d.id,
          factoryResetEnabled: factoryResetToggles[d.id] ?? false,
        })),
        ...(selectedStoryIds.length > 0 ? { selectedStoryIds } : {}),
        ...(skipInternet ? { skipInternet } : {}),
      });
      onTestStarted();
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error || err.message || "Failed to start test";
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit =
    !disabled &&
    targetVersion.trim() !== "" &&
    devices.length > 0 &&
    !isSubmitting;

  return (
    <DSBox sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <DSTextField
        label="Target Version"
        placeholder="e.g., 2.0.1"
        value={targetVersion}
        onChange={(e) => setTargetVersion(e.target.value)}
        fullWidth
      />

      {devices.length > 0 && (
        <DSBox sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <DSTypography variant="subtitle2" color="text.secondary">
            Factory Reset Options:
          </DSTypography>
          {devices.map((device) => (
            <DSFormControlLabel
              key={device.id}
              control={
                <DSSwitch
                  checked={factoryResetToggles[device.id] ?? false}
                  onChange={() => handleToggle(device.id)}
                />
              }
              label={`Factory reset: ${device.id}`}
            />
          ))}
        </DSBox>
      )}

      <StorySelector
        selectedStoryIds={selectedStoryIds}
        onSelectionChange={handleStorySelectionChange}
      />

      <DSFormControlLabel
        control={
          <DSCheckbox
            checked={skipInternet}
            onChange={(e) => setSkipInternet(e.target.checked)}
          />
        }
        label="No Internet"
      />

      <DSBox sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <DSButton
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          Run Test
        </DSButton>

        {isSubmitting && <DSCircularProgress size={24} />}
      </DSBox>

      {error && (
        <DSTypography variant="body2" color="error">
          {error}
        </DSTypography>
      )}
    </DSBox>
  );
};

export default TestForm;
